import { useDispatch, useSelector } from 'react-redux'
import Bugsnag, { NotifiableError } from '@bugsnag/js'
import React, { useRef, useEffect, useState, useCallback } from 'react'
import { PaymentRequest, PaymentRequestPaymentMethodEvent } from '@stripe/stripe-js'
import { RootState } from 'redux/store'
import { setPaymentMethod } from 'redux/features/paymentDetails'
import { ActionButton } from 'modules/ui/atoms/ActionButton'
import useFlow from 'modules/flow/state/use-flow'
import useOrder from 'modules/order/state/order/use-order'
import useFinalizeOrder from 'modules/order/finalize-order/use-finalize-order'
import useStripePaymentRequest from 'utils/functions/useStripePaymentRequest'
import { PaymentSelectionButtonProps } from 'modules/flow/components/payment-methods-step/components/payment-selection-button'
import { statesApi } from 'redux/services/states'
import { getClassySessionId } from 'modules/utils/getClassySessionId'
import { PaymentMethod } from 'models/ChargeIntent'

const DigitalWalletsButton = ({ color, children, ...restProps }: PaymentSelectionButtonProps) => {
  const canMakePayment = _useCanMakePayment()
  const paymentRequest = _useStripePaymentRequest()

  const onClick = useCallback(() => {
    if (paymentRequest) {
      // Show the digital wallet modal
      paymentRequest.show()
    }
  }, [paymentRequest])

  return canMakePayment && paymentRequest ? (
    <ActionButton type="button" color={color} onClick={onClick} {...restProps}>
      {children}
    </ActionButton>
  ) : (
    <></>
  )
}

export default DigitalWalletsButton

const _useCanMakePayment = () => {
  const canMakePayment = useSelector(
    (state: RootState) => state.order.stripePaymentRequest.canMakePayment,
  )

  return canMakePayment
}

const _useStripePaymentRequest = (): PaymentRequest | null => {
  const flow = useFlow()
  const order = useOrder()
  const dispatch = useDispatch()
  const finalizeOrder = useFinalizeOrder()
  const stripePaymentRequestPromise = useStripePaymentRequest()
  const [lazyStates] = statesApi.useLazyGetStatesQuery()

  const stripePaymentRequestLoadedRef = useRef(false)

  const [stripePaymentRequest, setStripePaymentRequest] = useState<PaymentRequest | null>(null)

  useEffect(() => {
    // Check to make sure it's only loaded once
    if (stripePaymentRequest || stripePaymentRequestLoadedRef.current) {
      return
    }

    stripePaymentRequestLoadedRef.current = true

    const onStripePaymentMethod = async (event: PaymentRequestPaymentMethodEvent) => {
      try {
        const { paymentMethod } = event

        dispatch(setPaymentMethod(paymentMethod))

        const address = paymentMethod.billing_details.address
        const names = paymentMethod.billing_details.name?.split(' ')
        let firstName = ''
        let lastName = ''

        if (names && names.length > 1) {
          firstName = names[0]
          lastName = names[names.length - 1]
        }

        const country = address?.country ?? ''
        let state = address?.state ?? ''

        // test if we actually got a ISO 3166-2 code (Apple Pay known to not do this)
        if (country && state && (state.length > 3 || state.toUpperCase() != state)) {
          const { data: states } = await lazyStates({
            countryCode: country,
          })

          // ultimately if we fail to find the right isoCode, donor will have to fill it out later
          state = states?.find((stateData) => state == stateData.name)?.isoCode ?? ''
        }

        // Set contact info
        const email = paymentMethod.billing_details?.email ?? ''
        order.contact.setValue({ ...order.contact.value, email, lastName, firstName })

        // Set address info
        order.address.setValue({
          city: paymentMethod.billing_details.address?.city ?? '',
          country: paymentMethod.billing_details.address?.country ?? '',
          line1: paymentMethod.billing_details.address?.line1 ?? '',
          zipCode: paymentMethod.billing_details.address?.postal_code ?? '',
          state: paymentMethod.billing_details.address?.state ?? '',
        })

        event.complete('success')

        if (window.newrelic) {
          window.newrelic.addPageAction('payment_method_selected', {
            'classy-session-id': getClassySessionId(),
            paymentMethod: PaymentMethod.DW,
          })
        }

        flow.nextStep && flow.goToStep(flow.nextStep.name)
      } catch (e) {
        event.complete('fail')
        Bugsnag.notify(e as NotifiableError)
      }
    }

    const initializePaymentRequest = async () => {
      const pr = await stripePaymentRequestPromise

      if (pr) {
        pr.on('paymentmethod', onStripePaymentMethod)
        setStripePaymentRequest(pr)
      }
    }

    initializePaymentRequest()
  }, [
    flow,
    dispatch,
    order.address,
    order.contact,
    finalizeOrder,
    stripePaymentRequest,
    stripePaymentRequestPromise,
  ])

  return stripePaymentRequest
}
